/**
 * Meta Pixel — client-side event firing for shop pages, CONSENT-GATED.
 *
 * Structural twin of tiktok-pixel.client.ts but deliberately NOT of its
 * consent behaviour: that plugin loaded unconditionally (fixed in the same
 * round). Here the vendor script does not touch the page until the visitor
 * grants the `marketing` cookie-consent category — loading fbq.js is itself
 * what sets the _fbp cookie, so "load now, gate later" is not compliance.
 *
 * Lifecycle:
 *   - no consent  → no script, no cookies; track() calls are dropped.
 *   - consent granted (at load or later via the banner) → inject fbq.js,
 *     init the tenant pixel, fire PageView + SPA page views on navigation.
 *   - consent revoked after load → fbq('consent','revoke') and every further
 *     call from us is suppressed. Cookies already set can only be removed by
 *     the browser/banner; revoke stops all further sending.
 *
 * Server-side counterpart: server/utils/metaEvents.ts fires Purchase from the
 * Stripe webhook. Dedup pairs browser+server via the shared deterministic
 * event id `order:<orderId>` (eventID option ↔ event_id field).
 *
 * Staff hosts (admin./creator./labels./scouts./superadmin.) never load the
 * pixel — buyers browse shop.* or the apex, and marketing trackers have no
 * business watching staff work.
 */
const STAFF_HOST_PREFIXES = ['admin.', 'creator.', 'labels.', 'scouts.', 'superadmin.', 'lnk.']

export default defineNuxtPlugin(() => {
    if (typeof window === 'undefined') return

    const noop = {
        provide: {
            metaPixel: {
                track: (_event: string, _props?: Record<string, any>, _eventId?: string) => {},
                page: () => {},
                enabled: false,
            },
        },
    }

    const host = window.location.host
    if (STAFF_HOST_PREFIXES.some(p => host.startsWith(p))) return noop

    const tenantState = useState<any>('tenant')
    const pixelId = tenantState.value?.metaPixel?.pixelId
    if (!pixelId) return noop

    const { state: consent } = useCookieConsent()
    let loaded = false
    let suppressed = false

    function loadPixel() {
        if (loaded) return
        loaded = true
        const w = window as any
        if (!w.fbq) {
            const n: any = (w.fbq = function (...args: any[]) {
                n.callMethod ? n.callMethod.apply(n, args) : n.queue.push(args)
            })
            if (!w._fbq) w._fbq = n
            n.push = n
            n.loaded = true
            n.version = '2.0'
            n.queue = []
            const s = document.createElement('script')
            s.async = true
            s.src = 'https://connect.facebook.net/en_US/fbevents.js'
            const x = document.getElementsByTagName('script')[0]
            x?.parentNode?.insertBefore(s, x)
        }
        w.fbq('consent', 'grant')
        w.fbq('init', pixelId)
        w.fbq('track', 'PageView')
    }

    // React to the banner in both directions. `immediate` covers a visitor
    // who granted consent on an earlier visit (cookie already set).
    watch(() => consent.value.marketing, (granted) => {
        if (granted) {
            suppressed = false
            loadPixel()
        } else if (loaded) {
            suppressed = true
            try { (window as any).fbq?.('consent', 'revoke') } catch {}
        }
    }, { immediate: true })

    const canSend = () => loaded && !suppressed

    const router = useRouter()
    router.afterEach(() => {
        if (!canSend()) return
        try { (window as any).fbq?.('track', 'PageView') } catch {}
    })

    return {
        provide: {
            metaPixel: {
                /**
                 * Fire a Meta pixel event ('ViewContent', 'AddToCart',
                 * 'InitiateCheckout', 'Purchase', …). Pass the same eventId
                 * as the server-side CAPI call so Meta dedupes the pair.
                 */
                track: (event: string, props?: Record<string, any>, eventId?: string) => {
                    if (!canSend()) return
                    try {
                        ;(window as any).fbq?.('track', event, props || {}, eventId ? { eventID: eventId } : undefined)
                    } catch (err) {
                        console.warn('[meta-pixel] track failed:', err)
                    }
                },
                page: () => {
                    if (!canSend()) return
                    try { (window as any).fbq?.('track', 'PageView') } catch {}
                },
                enabled: true,
            },
        },
    }
})
